<script setup lang="ts">
import { ID_KENO_FR } from '~/utils/constants/idsLotteries'

defineOptions({
	name: 'BlockNumbers',
})

interface Rule {
	min: number
	max: number
	total: number
	id: number
}

interface FlyingBall {
	id: string
	number: number
	startX: number
	startY: number
	endX: number
	endY: number
	isYellow: boolean
}

const props = withDefaults(
	defineProps<{
		modelValue?: (number | null)[]
		rule: Rule
		isRegularBall?: boolean
		isRepeatedNumbers?: boolean
		isAnimatingRandom?: boolean
		animatedNumbers?: number[]
		showAnimationFloatingBalls?: boolean
		classContainerNumbers?: string
		block?: string
		idLottery?: number | null
		isMutipleBets?: boolean
	}>(),
	{
		isRegularBall: true,
		isRepeatedNumbers: false,
		isAnimatingRandom: false,
		animatedNumbers: () => [],
		showAnimationFloatingBalls: true,
		isMutipleBets: false,
		idLottery: null,
		classContainerNumbers: 'flex flex-wrap justify-center sm:justify-start gap-x-7 sm:gap-x-2 gap-y-5 sm:gap-y-2',
	},
)

const emit = defineEmits(['update:modelValue', 'showAnimatingBalls', 'manualBallRevealed'])
const { t: $t } = useI18n()

const selectedNumbers = ref<(number | null)[]>([])
const flyingBalls = ref<FlyingBall[]>([])
const animatingBalls = ref<Set<number>>(new Set())

watch(
	() => props.modelValue,
	(newVal) => {
		selectedNumbers.value = newVal ? [...newVal] : []
	},
	{ immediate: true, deep: true },
)

const isDisabled = (ballNumber: number): boolean => {
	if (isSelectedBall(ballNumber)) return animatingBalls.value.has(ballNumber)
	return props.isAnimatingRandom || counterNumbers.value >= props.rule.total || animatingBalls.value.has(ballNumber)
}

const counterNumbers = computed(() => {
	return selectedNumbers.value.filter((n) => n !== null).length
})

const getNumbersBalls = computed(() => {
	const { min, max } = props.rule

	return crearRango(min, max)
})

const isShowSelectCustomer = computed(() => {
	if (props.idLottery === ID_KENO_FR) return true
	if (props.rule.total > 1 && props.isMutipleBets == true) return true
	return false
})

const ballColor = computed(() => {
	const regularBall = 'bg-white border-white'
	const classicBall = 'bg-yellow-300 border-yellow-300'
	const ball = props.isRegularBall ? regularBall : classicBall

	return `bg-opacity-20 border border-opacity-20 text-secondary-900 text-sm ${ball}`
})

const crearRango = (min: number, max: number) => {
	if (typeof min !== 'number' || typeof max !== 'number') return []

	if (min > max) return []

	return Array.from({ length: max - min + 1 }, (_, i) => i + min)
}

const findHeaderDestination = () => {
	if (!props.block) return
	const headerContainer = document.querySelector(`.${props.block}`)
	const firstIndexNotSelect = selectedNumbers.value.findIndex((num) => num === null)
	const elementHtml = headerContainer?.children[firstIndexNotSelect]
	if (!elementHtml) return

	const headerRect = elementHtml.getBoundingClientRect()

	return {
		x: headerRect.left + headerRect.width / 2,
		y: headerRect.top + headerRect.height / 2,
	}
}

const createFlyingBall = async (number: number, buttonElement: HTMLElement) => {
	const willBeSelected = !selectedNumbers.value.includes(number) && counterNumbers.value < props.rule.total
	const conditionalIsRepeat = props.isRepeatedNumbers ? false : !willBeSelected

	if (conditionalIsRepeat) return

	const buttonRect = buttonElement.getBoundingClientRect()
	const destination = findHeaderDestination()

	const flyingBall: FlyingBall = {
		id: `flying-${number}-${Date.now()}`,
		number,
		startX: buttonRect.left + buttonRect.width / 2,
		startY: buttonRect.top + buttonRect.height / 2,
		endX: destination?.x || 0,
		endY: destination?.y || 0,
		isYellow: !props.isRegularBall,
	}

	flyingBalls.value.push(flyingBall)

	return new Promise((resolve) => {
		setTimeout(() => {
			const index = flyingBalls.value.findIndex((ball) => ball.id === flyingBall.id)

			if (index > -1) {
				flyingBalls.value.splice(index, 1)
			}

			resolve(true)
		}, 900)
	})
}

const manualAnimation = (content: any) => {
	emit('manualBallRevealed', content)
}

const toggleSelection = (number: number, event: Event) => {
	if (props.isAnimatingRandom) return

	const existingIndex = selectedNumbers.value.indexOf(number)
	const isDeselecting = existingIndex > -1

	// Only check for animation if we're selecting (not deselecting)
	if (!isDeselecting && animatingBalls.value.has(number)) return

	const buttonElement = event.currentTarget as HTMLElement
	const conditionalIsRepeat = props.isRepeatedNumbers ? true : !isDeselecting

	// Only add to animating set if we're selecting
	if (conditionalIsRepeat && props.showAnimationFloatingBalls) {
		animatingBalls.value.add(number)
		createFlyingBall(number, buttonElement).then(() => {
			animatingBalls.value.delete(number)
			if (!props.isRepeatedNumbers) {
				const indexNumber = selectedNumbers.value.findIndex((num) => num === number)
				manualAnimation({ index: indexNumber, isYellow: !props.isRegularBall })
			} else {
				const indexNumber = selectedNumbers.value.findIndex((num) => num === null)
				const validateIndex = indexNumber - 1 < 0 ? props.rule.total - 1 : indexNumber - 1

				manualAnimation({ index: validateIndex, isYellow: !props.isRegularBall })
			}
		})
	}

	// Handle selection/deselection logic
	if (existingIndex > -1 && !props.isRepeatedNumbers) {
		selectedNumbers.value[existingIndex] = null

		manualAnimation({ index: existingIndex, isYellow: !props.isRegularBall, action: 'remove' })
	} else if (counterNumbers.value < props.rule.total) {
		const nullIndex = selectedNumbers.value.findIndex((num) => num === null)

		manualAnimation({ index: nullIndex, isYellow: !props.isRegularBall })

		if (nullIndex > -1) {
			selectedNumbers.value[nullIndex] = number
		} else {
			selectedNumbers.value.push(number)

			const indexCurrent = selectedNumbers.value.length - 1
			manualAnimation({ index: indexCurrent, isYellow: !props.isRegularBall })
		}
	} else if (existingIndex > -1 && props.isRepeatedNumbers) {
		selectedNumbers.value[existingIndex] = null
	}

	emit('update:modelValue', [...selectedNumbers.value])
}

const isSelectedBall = (number: number) => {
	if (props.isRepeatedNumbers) return false

	return selectedNumbers.value.includes(number)
}

const isDisabledBall = (number: number) => {
	const selectedCount = counterNumbers.value
	const isSelected = isSelectedBall(number)

	if (isSelected) return false
	return selectedCount >= props.rule.total || animatingBalls.value.has(number)
}

const shouldShowSelected = (number: number) => {
	if (!props.isAnimatingRandom) return isSelectedBall(number)

	return isSelectedBall(number) && props.animatedNumbers.includes(number)
}

const shouldUseEntranceAnimation = (number: number) => {
	return props.isAnimatingRandom && props.animatedNumbers.includes(number) && isSelectedBall(number)
}
</script>

<template>
	<div class="space-y-4 relative">
		<div class="flex justify-between sm:justify-start items-center sm:gap-14 font-semibold">
			<p class="leading-6 text-neutral-50">
				{{ isShowSelectCustomer ? $t('Select up to') : $t('Select') }} {{ props.rule.total }}
				<span v-if="props.rule.total === 1">{{ props.isRegularBall ? $t('main number') : $t('special number') }}</span>
				<span v-else> {{ props.isRegularBall ? $t('main numbers') : $t('special numbers') }}</span>
			</p>

			<p class="leading-5 text-xs text-neutral-50 text-opacity-60">
				{{ $t('Selected') }}: {{ counterNumbers }}/{{ props.rule.total }}
			</p>
		</div>

		<div :class="props.classContainerNumbers">
			<UButton
				v-for="number in getNumbersBalls"
				:key="number"
				color="white"
				variant="link"
				:disabled="isDisabled(number)"
				class="size-10 sm:size-12 flex items-center justify-center rounded-full font-semibold hover:bg-opacity-20 leading-5 hover:no-underline transition-all duration-300"
				:class="[
					ballColor,

					{
						'bg-opacity-100 hover:bg-opacity-90 focus:bg-opacity-90': shouldShowSelected(number),
						'hover:bg-opacity-70 hover:border-opacity-70': !isDisabledBall(number) && !props.isAnimatingRandom,
						'cursor-not-allowed opacity-50': props.isAnimatingRandom,
					},
				]"
				@click="toggleSelection(number, $event)"
			>
				<!-- Con animación de entrada (solo para try random) -->

				<Transition
					v-if="shouldUseEntranceAnimation(number)"
					name="bounce-block"
					mode="out-in"
				>
					<span
						v-if="shouldShowSelected(number)"
						:key="`animated-selected-${number}`"
						class="block"
					>
						{{ number }}
					</span>

					<span
						v-else
						:key="`animated-unselected-${number}`"
						class="block"
					>
						{{ number }}
					</span>
				</Transition>

				<!-- Sin animación de entrada (para selección manual y estado normal) -->

				<span
					v-else
					class="block"
				>
					{{ number }}
				</span>
			</UButton>
		</div>

		<!-- Bolitas voladoras -->

		<div class="flying-balls-container fixed top-0 left-0">
			<div
				v-for="ball in flyingBalls"
				:key="ball.id"
				class="flying-ball absolute size-10 rounded-full flex justify-center items-center font-bold text-sm text-secondary-900"
				:class="{
					'bg-white border-0': !ball.isYellow,
					'bg-yellow-300 border-2 border-yellow-300': ball.isYellow,
				}"
			>
				<!-- :style="{
					'--start-x': `${ball.startX}px`,
					'--start-y': `${ball.startY}px`,
					'--end-x': `${ball.endX}px`,
					'--end-y': `${ball.endY}px`,
				}" -->
				{{ ball.number }}
			</div>
		</div>
	</div>
</template>

<style scoped>
.bounce-block-enter-active {
	animation: bounce-block 0.7s;
}

@keyframes bounce-block {
	0% {
		opacity: 0;
		transform: translateX(-120%);
	}

	40% {
		opacity: 1;
		transform: translateX(0);
	}

	50% {
		transform: translateX(0);
	}

	75% {
		transform: translateX(0);
	}

	90% {
		transform: translateX(0);
	}
}

.bounce-block-leave-active {
	transition: all 0.3s;
}

.bounce-block-enter-from {
	opacity: 0;
	transform: translateX(-120%);
}

.bounce-block-leave-to {
	opacity: 0;
	transform: translateX(-120%);
}

.flying-balls-container {
	width: 100vw;
	height: 100vh;
	pointer-events: none;
	z-index: 1000;
}

.flying-ball {
	animation: fly-to-header 1.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
	left: var(--start-x);
	top: var(--start-y);
	transform: translate(-50%, -50%);
}

@keyframes fly-to-header {
	0% {
		left: var(--start-x);
		top: var(--start-y);
		opacity: 1;
		transform: translate(-50%, -50%) scale(1);
	}

	70% {
		left: var(--end-x);
		top: var(--end-y);
		opacity: 1;
		transform: translate(-50%, -50%) scale(0.8);
	}

	100% {
		left: var(--end-x);
		top: var(--end-y);
		opacity: 0;
		transform: translate(-50%, -50%) scale(0.3);
	}
}
</style>
